import './index.scss'

import { useAuth } from "@auth/AuthProvider";
import ErrorView from "@components/ErrorView";
import LoadingIndicator from "@components/LoadingIndicator";
import Modal from "@components/Modal";
import OptionsForm from "@components/OptionsForm";
import SuccessView from "@components/SuccessView";
import { useChurchDataProvider } from "@data/ChurchDataProvider";
import EventsController, { Event, ExternalWaiver, Waiver } from "@data/controller/EventsController";
import { ExternalVendor } from "@interfaces/external-vendor.interface";
import { useCallback, useEffect, useMemo, useState } from "react";

// MARK: - Icons

import waiversIcon from 'url:@assets/icons/waivers-icon.svg'
import smartwaiverIcon from 'url:@assets/images/smartwaiver-icon.svg'
import chevronLeft from 'url:@icon/chevron.left.svg';
import closeIcon from 'url:@icon/close.svg';
import chevronRight from 'url:@icon/chevron.right.plain.svg'
import pencilIcon from 'url:@assets/icons/pencil.svg'
import { HTTPResponse } from '@data/controller/NetworkController';

// MARK: - Components

export default function WaiverSetupModal(props: {
  event: Event,
  isOpen: boolean,
  updateEvent: (newEvent: Event) => void,
  close: () => void,
}) {
  const { connectionStatus, church } = useChurchDataProvider();
  const auth = useAuth();

  const [showManualWaiverCreation, setShowManualWaiverCreation] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<ExternalVendor | null>(null);
  const [selectedWaiver, setSelectedWaiver] = useState<ExternalWaiver | null>(null);

  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [externalWaivers, setExternalWaivers] = useState<ExternalWaiver[] | null>(null);
  const [externalWaiverLoadingError, setExternalWaiversLoadingError] = useState<string | null>(null);

  const [waiverInstructions, setWaiverInstructions] = useState<string | null>(null);

  const fetchExternalWaivers = useCallback(async () => {
    if (!selectedProvider || !church) return;

    try {
      const response = await EventsController.fetchAvailableExternalWaivers(church.id, selectedProvider, auth);

      if (response.body) {
        setExternalWaivers(response.body.externalWaivers);
        setExternalWaiversLoadingError(null);
      } else {
        setExternalWaivers(null);
        setExternalWaiversLoadingError(response.error || "Failed to load available waivers from that provider.");
      }
    } catch (error: any) {
      setExternalWaiversLoadingError(error);
    }
  }, [selectedProvider, church]);

  const attachExternalWaiver = async (externalWaiver: ExternalWaiver) => {
    save(() => EventsController.attachExternalWaiverToEvent(props.event.churchID, props.event.id, externalWaiver, waiverInstructions, auth));
  };

  const createWaiver = async (name: string, url: string, instructions: string | null) => {
    save(() => EventsController.createManualWaiverForEvent(props.event.churchID, props.event.id, { name, url, instructions }, auth));
  }

  const save = async (action: () => Promise<HTTPResponse<{ event: Event }>>) => {
    setSaving(true);

    try {
      const response = await action();
      if (response.error) {
        setSaveError(response.error);
        return;
      }

      const body = response.body;
      if (body) {
        props.updateEvent(body.event);
      }

      setShowSuccess(true);
    } catch (e: any) {
      setSaveError(e);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!props.isOpen) return;
    setSaveError(null);
    setSaving(false);
    setShowSuccess(false);
    setSelectedProvider(null);
    setExternalWaivers(null);
    setExternalWaiversLoadingError(null);
    setSelectedWaiver(null);
    setShowManualWaiverCreation(false);
  }, [props.isOpen]);

  const optionsForm = <OptionsForm
    icon={{
      image: waiversIcon,
      alt: 'Waivers'
    }}
    title='Add Waiver'
    description='Get started by selecting a waiver provider.'
    unavailableText={<>Unavailable creation methods may require you to connect an external provider in <a href='/connections'>Connections</a></>}
    cards={[
      {
        title: 'Smartwaiver',
        logo: {
          image: smartwaiverIcon,
          alt: 'Smartwaiver'
        },
        description: 'Import a Smartwaiver template',
        enabled: connectionStatus?.SMART_WAIVER === true,
        onClick: () => {
          setSelectedProvider(ExternalVendor.SMART_WAIVER);
        }
      },
      {
        title: "Manual Waiver",
        description: "Create a waiver that must be manually updated when completed by each attendee",
        enabled: true,
        logo: {
          image: pencilIcon,
          alt: 'Manual Waiver'
        },
        onClick: () => {
          setShowManualWaiverCreation(true);
        }
      }
    ]} />

  const closeButton = <a className="close"
    href="#"
    onClick={(e) => {
      e.preventDefault();
      props.close()
    }}>
    <img className="icon" src={closeIcon} alt="Close icon" />
  </a>;

  const backButton = <a className="back"
    href="#"
    onClick={(e) => {
      e.preventDefault();

      if (selectedWaiver) {
        setSelectedWaiver(null);
      } else {
        setSelectedProvider(null);
        setExternalWaivers(null);
        setExternalWaiversLoadingError(null);
        setShowManualWaiverCreation(false);
      }
    }}>
    <img className="icon" src={chevronLeft} alt="Left arrow icon" /> Back
  </a>;

  return <Modal id="waiver-setup"
    isOpen={props.isOpen}
    footerButtons={[]}>
    <div style={{
      paddingTop: 32
    }}>
      {saving || showSuccess || saveError ? <>
        {saving && <LoadingIndicator fullScreen={false} />}

        {saveError && <div>
          {closeButton}
          <ErrorView error={saveError} showButton={false} />
        </div>}

        {showSuccess && <div>
          {closeButton}
          <SuccessView
            title='Waiver Added'
            description={`Waiver successfully added to ${props.event.title}.`} />
        </div>}
      </> : <>
        <div className='buttons'>
          {selectedProvider && backButton}
          {showManualWaiverCreation && backButton}

          {!selectedProvider && !showManualWaiverCreation && closeButton}
        </div>

        {!connectionStatus && <LoadingIndicator fullScreen={false} />}

        {selectedProvider === null && connectionStatus && !showManualWaiverCreation ? optionsForm : null}

        {selectedProvider && !selectedWaiver && <ExternalWaiverList event={props.event}
          selectedProvider={selectedProvider}
          externalWaiverLoadingError={externalWaiverLoadingError}
          externalWaivers={externalWaivers}
          fetchExternalWaivers={fetchExternalWaivers}
          externalWaiverSelected={(waiver) => {
            setSelectedWaiver(waiver)
          }} />}

        {selectedWaiver && <div className='selectedWaiverDetails'>
          <h3>Instructions for {selectedWaiver.name}</h3>
          <p>If you would like to display specific instructions for this waiver, enter them below. Then, click the Attach button to complete setup.</p>

          <textarea placeholder='Custom Waiver Instructions' value={waiverInstructions || ""} onChange={(e) => {
            setWaiverInstructions(e.target.value);
          }} />

          <button onClick={(e) => {
            e.preventDefault();
            attachExternalWaiver(selectedWaiver);
          }}>Attach Waiver to Event</button>
        </div>}

        {showManualWaiverCreation && <ManualWaiverSetup onCreateClicked={(name, url, instructions) => {
          createWaiver(name, url, instructions);
        }} />}
      </>}
    </div>
  </Modal>
}

function ManualWaiverSetup(props: {
  onCreateClicked: (name: string, url: string, instructions: string | null) => void
}) {
  const [name, setName] = useState("");
  const [url, setURL] = useState("");
  const [instructions, setInstructions] = useState<string | null>(null);

  const canCreate = name.length > 0 && url.length > 0;

  return <div className='manualWaivers'>
    <div className='header'>
      <h3>Manual Waiver Setup</h3>
      <p>Manual Waivers should be created for waivers that don't directly integrate with Salt.</p>
    </div>

    <form>
      <p className='heading' style={{ marginBottom: '0.5rem' }}>Information</p>
      <div className='section'>
        <input type='text' name='waiverName' placeholder='Waiver Name' value={name || ''} onChange={(e) => setName(e.target.value)} />

        <Separator />

        <input type='text' name='waiverURL' placeholder='Waiver URL' value={url || ''} onChange={(e) => setURL(e.target.value)} />

        <Separator />

        <textarea placeholder='Instructions' name='instructions' value={instructions || ''} onChange={(e) => setInstructions(e.target.value)} />
      </div>

      <p className='footnote'>Instructions will be sent alongside attendee Waiver Reminder emails.</p>
    </form>

    <button onClick={(e) => {
      e.preventDefault();
      if (!canCreate) return;

      props.onCreateClicked(name, url, instructions);
    }} disabled={!canCreate}>Create Waiver</button>
  </div>
}

function ExternalWaiverList(props: {
  event: Event,
  selectedProvider: ExternalVendor,
  externalWaivers: ExternalWaiver[] | null,
  externalWaiverLoadingError: string | null,
  fetchExternalWaivers: () => Promise<void>,
  externalWaiverSelected: (externalWaiver: ExternalWaiver) => void
}) {
  const prettyProviderName = useMemo(() => {
    if (!props.selectedProvider) return '';

    switch (props.selectedProvider) {
      case ExternalVendor.SMART_WAIVER: return 'Smartwaiver';
      default: return null;
    }
  }, [props.selectedProvider]);

  useEffect(() => {
    let mounted = true;

    const performFetch = async () => {
      await props.fetchExternalWaivers();
      if (!mounted) return;
    };

    performFetch();

    return () => {
      mounted = false;
    }
  }, []);

  return <>
    {!props.externalWaivers && <LoadingIndicator fullScreen={false} />}

    {
      props.externalWaivers && <div className='availableExternalWaivers'>
        <h3>Available Waivers from {prettyProviderName}</h3>
        <p>Select a waiver to attach to {props.event.title}.</p>

        {props.externalWaiverLoadingError && <p className='error'>{props.externalWaiverLoadingError}</p>}

        {props.externalWaivers.length > 0 ? <div style={{ marginTop: 16 }}>
          <div className='card'>
            {props.externalWaivers.map((waiver, idx) => {
              const existingWaiver = props.event.waivers.find((existingWaiver: Waiver) => {
                return existingWaiver.externalMetadata?.externalID == waiver.externalID
              });
              const alreadyAdded = existingWaiver !== undefined;

              return <div key={waiver.externalID}>
                {idx !== 0 ? <div className='separator' /> : null}

                <div style={{ padding: 8, paddingTop: 16, paddingBottom: 16 }}
                  role='button'
                  className={'waiver-option ' + (alreadyAdded ? "existing" : "")}
                  onClick={(e) => {
                    e.preventDefault();

                    if (alreadyAdded) return;
                    props.externalWaiverSelected(waiver);
                  }}>
                  <div className='text'>
                    <p style={{ margin: 0, fontWeight: 500 }}>{waiver.name}</p>
                    {alreadyAdded ?
                      <p className='existing-warning'>This waiver is already attached to this event.</p> :
                      <a target='_blank' rel='noopener noreferrer' href={waiver.url}>{waiver.url}</a>
                    }
                  </div>
                  <img src={chevronRight} className='chevron' alt='' />
                </div>
              </div>
            })}
          </div>
        </div> : null}
      </div>
    }
  </>
}

function Separator() {
  return <div style={{
    width: "100%",
    height: 1,
    backgroundColor: "#eaeaea",
    opacity: 0.10,
    margin: '12px 0'
  }} />
}
